import { TestLocators } from 'constants/Locators';

import { MUI, MUIIcons, ThemeMode } from 'ci-esi-web-shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectUserInfo } from 'app/containers/PrivateRoute/selectors';
import { notificationsSectionClick, headerNavClick as trackEvent } from 'common/analytics';
import { colors } from 'styles/theme/colors';
import { ProductUpdateType, ProductUpdateTypeGA } from 'types/ProductUpdate';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';

import { NotificationBody } from './NotificationBody';
import { NotificationsWindow } from './NotificationsWindow';
import { notificationsSaga } from './saga';
import { selectData, selectStatus } from './selectors';
import {
  actions as notificationsActions,
  sliceKey as notificationsKey,
  reducer as notificationsReducer,
} from './slice';

const Notifications = () => {
  useInjectReducer({ key: notificationsKey, reducer: notificationsReducer });
  useInjectSaga({ key: notificationsKey, saga: notificationsSaga });

  const theme = MUI.useTheme();
  const isDarkMode = theme.palette.mode === ThemeMode.DARK;

  const dispatch = useDispatch();
  const status = useSelector(selectStatus);
  const notifications = useSelector(selectData);
  const userInfo = useSelector(selectUserInfo);

  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isNotificationsWindowOpen, setIsNotificationsWindowOpen] = useState(false);
  const [showMarkAllAsRead, setShowMarkAllAsRead] = useState(false);
  const [notificationsLightCheck, setNotificationsLightCheck] = useState(false);

  const toggleNotifications = useCallback((isOpen: boolean) => {
    setIsNotificationsOpen(isOpen);
    setNotificationsLightCheck(true);
  }, []);

  useEffect(() => {
    dispatch(notificationsActions.loadNotifications({ productUpdateType: ProductUpdateType.Notification }));
  }, [dispatch]);

  useEffect(() => {
    setShowMarkAllAsRead(notifications.some(({ isRead }) => !isRead));
  }, [notifications, setShowMarkAllAsRead]);

  const hasNewNotifications = useMemo(() => {
    const impactLastVisitedDate = userInfo?.impactLastVisited ? new Date(userInfo?.impactLastVisited).getTime() : 0;
    const latestNotificationDate = notifications[0]?.releaseDate
      ? new Date(notifications[0]?.releaseDate).getTime()
      : 0;
    const notificationsUpToDate = !!notifications.length && impactLastVisitedDate < latestNotificationDate;

    return notificationsUpToDate && !notificationsLightCheck;
  }, [notifications, notificationsLightCheck, userInfo?.impactLastVisited]);

  const handleAllAsRead = () => {
    dispatch(notificationsActions.trackAllNotificationsAsViewed());
    setShowMarkAllAsRead(false);
  };

  const handleSelectNotificationsCategory = useCallback(
    (category: ProductUpdateType) => {
      setIsNotificationsWindowOpen(true);
      toggleNotifications(false);
      dispatch(notificationsActions.setActiveProductUpdateInWindow(category));
      notificationsSectionClick(ProductUpdateTypeGA[category]);
    },
    [setIsNotificationsWindowOpen, toggleNotifications, dispatch],
  );

  const notificationIconSx = {
    position: 'relative' as const,
    cursor: 'pointer',
    width: 32,
    height: 32,
    padding: '4px 6px',
    border: `1px ${colors.grey62} solid`,
    borderRadius: '5px',
    color: isDarkMode ? colors.grey92 : colors.grey40,
  };

  const dotIndicatorSx = {
    display: 'inline-block',
    width: 8,
    height: 8,
    backgroundColor: colors.stateWarningDefault,
    borderRadius: '50%',
  };

  const notificationIconDotSx = {
    position: 'absolute' as const,
    right: '2px',
    top: '2px',
  };

  const notificationsPaperSx = {
    background: isDarkMode ? colors.grey40 : colors.primaryWhite,
    top: '66px',
    right: '7px',
    height: 'calc(100% - 81px)',
    borderRadius: '0 0 2px 2px',
  };

  const notificationsBackdropSx = {
    backgroundColor: 'transparent',
  };

  return (
    <>
      <div
        onClick={() => {
          trackEvent('Bell Icon');
          toggleNotifications(true);
        }}
        data-testid={TestLocators.Landing_OpenNotificationsPanel}
      >
        <MUI.Box sx={notificationIconSx}>
          {hasNewNotifications && <MUI.Box sx={{ ...dotIndicatorSx, ...notificationIconDotSx }} />}
          {isNotificationsOpen ? (
            <MUIIcons.NotificationsActiveSharp style={{ fontSize: '18px' }} />
          ) : (
            <MUIIcons.NotificationsSharp style={{ fontSize: '18px' }} />
          )}
        </MUI.Box>
      </div>
      <NotificationsWindow
        notifications={notifications}
        isOpen={isNotificationsWindowOpen}
        hasNewNotifications={hasNewNotifications}
        handleAllAsRead={handleAllAsRead}
        onClose={() => setIsNotificationsWindowOpen(false)}
        handleSelectNotificationsCategory={handleSelectNotificationsCategory}
      />
      <MUI.Drawer
        anchor="right"
        open={isNotificationsOpen}
        onClose={() => toggleNotifications(false)}
        BackdropProps={{ sx: notificationsBackdropSx }}
        PaperProps={{ sx: notificationsPaperSx }}
      >
        <NotificationBody
          status={status}
          notifications={notifications}
          showMarkAllAsRead={showMarkAllAsRead}
          handleAllAsRead={handleAllAsRead}
          handleSelectNotificationsCategory={handleSelectNotificationsCategory}
        />
      </MUI.Drawer>
    </>
  );
};

export default Notifications;
